import i18next from "i18next";
import { getApiRequest } from "../services/getApiRequest";
import { END_POINTS } from "../Helper/Constant";
import { setSelectedGames } from "../features/duelsGameSelection/gameSlice";

export const getLocalStorageData = (key) => {
  return localStorage.getItem(key);
};

export const setLocalStorageData = (key, value) => {
  localStorage.setItem(key, value);
};

export const clearLocalStorageData = () => {
  localStorage.clear();
};

export const removeLocalStorageParticularKey = (key) => {
  localStorage.removeItem(key);
};

export const getSessionJSON = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? value : null;
  } catch (error) {
    console.error(`Invalid JSON in sessionStorage for key: ${key}`, error);
    return null;
  }
};

export const setSessionJSON = (key, data) => {
  try {
    sessionStorage.setItem(key, data);
  } catch (error) {
    console.error(
      `Failed to set JSON in sessionStorage for key: ${key}`,
      error
    );
  }
};

export const getCountryText = (allowedCountries = [], t) => {
  if (allowedCountries.length === 0) {
    return t ? t("past_tournament_tab.all_regions") : "All Regions";
  }

  if (allowedCountries.length === 1) {
    return i18next.language === "ar" &&
      allowedCountries[0]?.name === "Gulf Cooperation Council"
      ? "مجلس التعاون الخليجي"
      : i18next.language === "en" &&
        allowedCountries[0]?.name === "Gulf Cooperation Council"
        ? "GCC"
        : allowedCountries[0]?.name;
  }

  if (allowedCountries.length === 2) {
    return allowedCountries
      .map((country) =>
        i18next.language === "ar" &&
          country?.name === "Gulf Cooperation Council"
          ? "مجلس التعاون الخليجي"
          : i18next.language === "en" &&
            country?.name === "Gulf Cooperation Council"
            ? "GCC"
            : country?.name
      )
      .join(" - ");
  }

  return allowedCountries
    .map((country) =>
      i18next.language === "ar" && country?.alpha2Code === "GCC"
        ? "مجلس التعاون الخليجي"
        : i18next.language === "en" && country?.alpha2Code === "GCC"
          ? "GCC"
          : country?.alpha2Code
    )
    .join(" - ");
};

export const splitTextIntoChunks = (text) => {
  const words = text.split(" ");
  let chunks = [];
  for (let i = 0; i < words.length; i += 3) {
    chunks.push(words.slice(i, i + 3).join(" "));
  }
  return chunks || " ";
};


export const shouldShowRewardPopup = (STORAGE_KEY) => {
  const today = new Date().toISOString().slice(0, 10);
  const lastSeen = localStorage.getItem(STORAGE_KEY);

  return lastSeen !== today;
};

export const markRewardPopupSeen = (STORAGE_KEY) => {
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(STORAGE_KEY, today);
};

export const ACHIEVEMENT_TIERS = ["bronze", "silver", "gold", "diamond"];

// The tier a user is currently working towards is one above their highest unlocked tier —
// "none" means they're working towards bronze, "bronze" means silver, and so on.
export const getActiveAchievementTierIndex = (highestTier) => {
  if (!highestTier || highestTier === "none") return 0;
  const unlockedIndex = ACHIEVEMENT_TIERS.indexOf(highestTier);
  if (unlockedIndex < 0) return 0;
  return Math.min(unlockedIndex + 1, ACHIEVEMENT_TIERS.length - 1);
};

export const hasUnlockedAchievementTier = (highestTier) =>
  Boolean(highestTier && highestTier !== "none" && ACHIEVEMENT_TIERS.includes(highestTier));

export const STARTER_ACHIEVEMENT_KEYS = [
  "tournament-contender",
  "competitive-spirit",
  "rising-celebrity",
  "never-miss-a-day",
  "team-player",
  "goat",
];

export const GOAT_ACHIEVEMENT_KEY = "goat";

export const normalizeAchievementKey = (key) =>
  String(key || "").trim().toLowerCase().replace(/_/g, "-");

export const isStarterAchievementKey = (key) =>
  STARTER_ACHIEVEMENT_KEYS.includes(normalizeAchievementKey(key));

export const hasAchievementBronzeOrHigher = (highestTier) =>
  hasUnlockedAchievementTier(highestTier);

export const getStarterBronzeCount = (achievements = []) =>
  achievements.filter(
    (item) =>
      isStarterAchievementKey(item?.achievement?.key) &&
      hasAchievementBronzeOrHigher(item?.progress?.highestTier)
  ).length;

export const hasGoatBronzeAchievement = (achievements = []) =>
  achievements.some(
    (item) =>
      normalizeAchievementKey(item?.achievement?.key) === GOAT_ACHIEVEMENT_KEY &&
      hasAchievementBronzeOrHigher(item?.progress?.highestTier)
  );

export const areRemainingAchievementsUnlocked = (achievements = []) =>
  hasGoatBronzeAchievement(achievements) || getStarterBronzeCount(achievements) >= 5;

export const sortAchievementsByStarterOrder = (achievements = []) => {
  const starterOrder = new Map(
    STARTER_ACHIEVEMENT_KEYS.map((key, index) => [key, index])
  );

  return [...achievements].sort((a, b) => {
    const aKey = normalizeAchievementKey(a?.achievement?.key);
    const bKey = normalizeAchievementKey(b?.achievement?.key);
    const aIsStarter = starterOrder.has(aKey);
    const bIsStarter = starterOrder.has(bKey);

    if (aIsStarter && bIsStarter) {
      return starterOrder.get(aKey) - starterOrder.get(bKey);
    }
    if (aIsStarter) return -1;
    if (bIsStarter) return 1;

    return (a?.achievement?.sortOrder ?? 0) - (b?.achievement?.sortOrder ?? 0);
  });
};

